import { useRef, useState } from "react";
import axios from "axios";
import { CheckCircle2, FileUp, Loader2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PCB_TYPE_OPTIONS = ["Single Layer PCB", "Double Layer PCB", "Multilayer PCB", "Metal Core PCB", "Not Sure / Need Advice"];

const EMPTY = {
    name: "",
    company: "",
    phone: "",
    email: "",
    pcb_requirement: "",
    pcb_type: "",
    quantity: "",
    message: "",
};

const labelCls = "mb-1.5 block font-mono text-[10.5px] font-medium uppercase tracking-[0.18em] text-slate-500";
const inputCls = "h-11 rounded-none border-slate-300 bg-white text-[14px] text-navy focus-visible:ring-brand-red focus-visible:ring-offset-0";

export default function EnquiryForm() {
    const [form, setForm] = useState(EMPTY);
    const [file, setFile] = useState(null);
    const [status, setStatus] = useState("idle");
    const [result, setResult] = useState(null);
    const fileRef = useRef(null);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const onFile = (e) => {
        const f = e.target.files && e.target.files[0];
        setFile(f || null);
    };

    const removeFile = () => {
        setFile(null);
        if (fileRef.current) fileRef.current.value = "";
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
            toast.error("Please fill in name, phone number and email.");
            return;
        }
        setStatus("submitting");
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        if (file) fd.append("gerber_file", file);
        try {
            const res = await axios.post(`${API}/enquiries`, fd);
            setStatus("success");
            setResult(res.data);
            setForm(EMPTY);
            setFile(null);
            toast.success("Enquiry submitted successfully. Our team will contact you shortly.");
        } catch (err) {
            setStatus("idle");
            toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
        }
    };

    if (status === "success") {
        return (
            <div className="flex flex-col items-start gap-4 py-6" data-testid="enquiry-success-panel">
                <CheckCircle2 className="h-12 w-12 text-green-600" />
                <div>
                    <h3 className="text-xl font-semibold text-navy">Enquiry Received</h3>
                    <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-600">
                        Thank you for your interest in Unimicron Technology India
                        {result?.gerber_attached ? " — your Gerber data has been attached to the enquiry." : "."}
                        {" "}Our team will review your requirement and respond shortly.
                    </p>
                    <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-slate-400">
                        Reference: {result?.enquiry_id?.slice(0, 8).toUpperCase()}
                    </p>
                </div>
                <Button
                    onClick={() => { setStatus("idle"); setResult(null); }}
                    variant="outline"
                    className="rounded-none border-navy text-navy hover:bg-navy hover:text-white"
                    data-testid="enquiry-send-another-btn"
                >
                    Send Another Enquiry
                </Button>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="enquiry-form">
            <div>
                <label className={labelCls} htmlFor="qf-name">Name *</label>
                <Input id="qf-name" value={form.name} onChange={set("name")} placeholder="Your full name" required data-testid="enquiry-name-input" className={inputCls} />
            </div>
            <div>
                <label className={labelCls} htmlFor="qf-company">Company Name</label>
                <Input id="qf-company" value={form.company} onChange={set("company")} placeholder="Company / firm" data-testid="enquiry-company-input" className={inputCls} />
            </div>
            <div>
                <label className={labelCls} htmlFor="qf-phone">Phone Number *</label>
                <Input id="qf-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 XXXXX XXXXX" required data-testid="enquiry-phone-input" className={inputCls} />
            </div>
            <div>
                <label className={labelCls} htmlFor="qf-email">Email *</label>
                <Input id="qf-email" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" required data-testid="enquiry-email-input" className={inputCls} />
            </div>
            <div>
                <label className={labelCls} htmlFor="qf-requirement">PCB Requirement</label>
                <Input id="qf-requirement" value={form.pcb_requirement} onChange={set("pcb_requirement")} placeholder="e.g. 2-layer control board, FR-4, 1.6mm" data-testid="enquiry-requirement-input" className={inputCls} />
            </div>
            <div>
                <label className={labelCls} htmlFor="qf-type">PCB Type</label>
                <select
                    id="qf-type"
                    value={form.pcb_type}
                    onChange={set("pcb_type")}
                    data-testid="enquiry-type-select"
                    className={`${inputCls} appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2364748b%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[position:right_0.75rem_center] bg-no-repeat pr-9`}
                >
                    <option value="">Select PCB type</option>
                    {PCB_TYPE_OPTIONS.map((o) => (
                        <option key={o} value={o}>{o}</option>
                    ))}
                </select>
            </div>
            <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="qf-quantity">Quantity</label>
                <Input id="qf-quantity" value={form.quantity} onChange={set("quantity")} placeholder="e.g. 100 Nos. (or prototype: 5 Nos.)" data-testid="enquiry-quantity-input" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="qf-message">Message</label>
                <Textarea id="qf-message" value={form.message} onChange={set("message")} placeholder="Tell us about your application, board size, copper thickness or any other detail..." rows={3} data-testid="enquiry-message-input" className="rounded-none border-slate-300 bg-white text-[14px] text-navy focus-visible:ring-brand-red focus-visible:ring-offset-0" />
            </div>
            <div className="sm:col-span-2">
                <label className={labelCls}>Gerber Data Upload</label>
                <label
                    htmlFor="qf-gerber"
                    data-testid="enquiry-gerber-dropzone"
                    className="flex cursor-pointer items-center justify-between gap-4 border border-dashed border-slate-300 bg-slate-50 px-4 py-3.5 transition-colors hover:border-brand-red"
                >
                    <span className="flex items-center gap-3 text-[13px] text-slate-500">
                        <FileUp className="h-5 w-5 text-brand-red" />
                        {file ? (
                            <span className="flex items-center gap-2 text-navy" data-testid="enquiry-gerber-file-name">
                                {file.name}
                                <span className="font-mono text-[10px] uppercase text-slate-400">
                                    {(file.size / 1024).toFixed(0)} KB
                                </span>
                            </span>
                        ) : (
                            "Attach Gerber / drill files — ZIP, RAR, PDF, GBR, DRL (max 15 MB)"
                        )}
                    </span>
                    {file ? (
                        <button type="button" onClick={(e) => { e.preventDefault(); removeFile(); }} aria-label="Remove file" data-testid="enquiry-gerber-remove-btn" className="p-1 text-slate-400 transition-colors hover:text-brand-red">
                            <X className="h-4 w-4" />
                        </button>
                    ) : (
                        <span className="font-mono text-[10px] uppercase tracking-widest text-brand-red">Browse</span>
                    )}
                    <input ref={fileRef} id="qf-gerber" type="file" accept=".zip,.rar,.7z,.tar,.gz,.tgz,.gbr,.ger,.gerber,.gbl,.gtl,.gbs,.gbo,.gko,.drl,.drd,.txt,.pdf,.brd,.pcb,.pcbdoc" onChange={onFile} className="hidden" data-testid="enquiry-gerber-input" />
                </label>
            </div>
            <Button
                type="submit"
                disabled={status === "submitting"}
                data-testid="enquiry-submit-btn"
                className="col-span-1 h-12 rounded-none border border-brand-red bg-brand-red text-[13px] font-semibold uppercase tracking-wider text-white transition-colors hover:bg-brand-redhover sm:col-span-2"
            >
                {status === "submitting" ? (
                    <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</span>
                ) : (
                    "Submit Enquiry"
                )}
            </Button>
        </form>
    );
}
